/**
 * Change Order — renderer (frontend). Same shape as PurchaseOrder.tsx.
 * Reads the snapshot payload produced by backend/document-sharing/types/change-order.js.
 */
import type { DocumentProps, FrontendDocumentDefinition } from '../registry';
import { BaseDocumentLayout } from '../BaseDocumentLayout';

function formatCurrency(value: any): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatDate(value: any): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const MUTED = 'rgb(var(--gray-500))'; // was #6b7280 — exact match to --gray-500 token

export const documentDefinition: FrontendDocumentDefinition = {
  docType: 'change_order',
  renderer: ChangeOrder,
  appRoute: '/project-management/change-orders',
};

export function ChangeOrder({ payload }: DocumentProps) {
  const co = payload?.change_order || {};
  const company = payload?.company || null;
  const project = payload?.project || null;
  const client = payload?.client || null;

  return (
    <BaseDocumentLayout
      company={company}
      title="CHANGE ORDER"
      reference={`#${co.co_number}`}
      meta={[
        { label: 'Status', value: co.status },
        { label: 'Approval deadline', value: formatDate(co.approval_deadline) },
      ]}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 20 }}>
        <div style={{ flex: '1 1 240px' }}>
          {/* gap: this field-label style (fontSize 11 / uppercase / marginBottom 4) is
              repeated identically across all 6 document templates — candidate for a
              shared "doc field label" token/class, not added here per scope. */}
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Client</div>
          <div style={{ fontWeight: 600 }}>{client?.name || 'Client'}</div>
          {client?.email && <div style={{ color: MUTED }}>{client.email}</div>}
          {client?.address && <div style={{ color: MUTED }}>{client.address}</div>}
        </div>
        <div style={{ flex: '1 1 240px' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED, marginBottom: 4 }}>Project</div>
          <div style={{ fontWeight: 600 }}>{project?.name || '—'}</div>
        </div>
      </div>

      {co.title && <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{co.title}</div>}
      {co.description && <div style={{ whiteSpace: 'pre-wrap', marginBottom: 20 }}>{co.description}</div>}

      {/* gap: this footer total block (border-top 2px + paddingTop 12, fontSize 20/700
          value) is repeated identically across 5 of 6 document templates (all but
          Warranty) — candidate for a shared "doc total footer" token/component. */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '2px solid rgb(var(--gray-900))', paddingTop: 12 }}>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: MUTED }}>Client Cost</div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{formatCurrency(co.client_cost)}</div>
        </div>
      </div>
    </BaseDocumentLayout>
  );
}
